import { Controller } from "@hotwired/stimulus"
import { loadPdf as openPdf } from "../helpers/load_pdf"
import Konva from "konva"

// Tools that live inside a split-button flyout group on the toolbar
const TOOL_GROUPS = {
  freehand: "draw",
  line: "draw",
  arrow: "draw",
  rect: "shapes",
  ellipse: "shapes",
  checkmark: "stamps",
  xmark: "stamps",
}

export default class extends Controller {
  static targets = [
    "editor",
    "container",
    "toolbar",
    "filename",
    "colorPicker",
    "fontSizeInput",
    "fontStyleSelect",
    "saveStatus",
    "loadingSpinner",
    "zoomLevel",
    "fillToggle",
    "historySidebar",
    "historyContent",
    "pdfArea",
    "modeButton",
    "editZone",
    "toolGroup",
    "fontControls",
    "fillControls",
  ]

  static values = {
    documentFileId: Number,
    pdfUrl: String,
    annotationUrl: String,
    downloadUrl: String,
    siteId: Number,
    fileType: { type: String, default: "pdf" },
    i18nPage: { type: String, default: "Seite" },
    i18nComment: { type: String, default: "Kommentar" },
    i18nCreateTask: { type: String, default: "Task erstellen" },
    i18nNoSite: { type: String, default: "Kein Bauvorhaben zugeordnet." },
    i18nTextPrompt: { type: String, default: "Text:" },
    i18nSaving: { type: String, default: "Speichert..." },
    i18nSaved: { type: String, default: "Gespeichert" },
    i18nError: { type: String, default: "Fehler" },
  }

  connect() {
    this.stages = {} // page number -> Konva.Stage
    this.layers = {} // page number -> Konva.Layer
    this.currentTool = "view"
    this._lastEditTool = "select"
    this.currentColor = "#FF0000"
    this.currentFontSize = 16
    this.currentFontStyle = "normal"
    this.undoStack = []
    this.markerCounter = 0
    this.saveTimeout = null
    this.selectedNode = null
    this.transformer = null
    this.scale = 1
    this.baseScale = 1
    this.zoomLevel = 1.0
    this.pdfDocument = null
    this.pageViewports = {} // page number -> {width, height}
    this.fillEnabled = false
    // Drag-draw state for arrow/line
    this.isDragDrawing = false
    this.dragDrawStart = null
    this.dragDrawLayer = null
    this.dragPreview = null
    // Task annotation state
    this.pendingTaskPosition = null
    this._handleTaskCreated = this.handleTaskCreated.bind(this)
    window.addEventListener("pdf-annotation:task-created", this._handleTaskCreated)
    // History sidebar state
    this._historyOpen = false
    // History highlight
    this._handleHighlightRequest = this.handleHighlightRequest.bind(this)
    document.addEventListener("pdf-annotation:highlight-request", this._handleHighlightRequest)
    // Close tool-group flyouts on outside click
    this._outsideClickHandler = (e) => {
      if (!e.target.closest("[data-group]")) this.closeGroupFlyouts()
    }
    document.addEventListener("click", this._outsideClickHandler)
  }

  disconnect() {
    this.resetEditorPosition()
    Object.values(this.stages).forEach((s) => s.destroy())
    this.stages = {}
    this.layers = {}
    window.removeEventListener("pdf-annotation:task-created", this._handleTaskCreated)
    document.removeEventListener("pdf-annotation:highlight-request", this._handleHighlightRequest)
    document.removeEventListener("click", this._outsideClickHandler)
    if (this._offcanvasAttrObserver) {
      this._offcanvasAttrObserver.disconnect()
      this._offcanvasAttrObserver = null
    }
  }

  // ──────────────────────────────────────────────────
  // Open / Close
  // ──────────────────────────────────────────────────
  open(event) {
    const btn = event.currentTarget
    this.documentFileIdValue = parseInt(btn.dataset.documentFileId)
    this.pdfUrlValue = btn.dataset.pdfUrl
    this.annotationUrlValue = `/document_files/${this.documentFileIdValue}/pdf_annotation`
    this.downloadUrlValue = `/document_files/${this.documentFileIdValue}/pdf_annotation/download`
    if (btn.dataset.siteId) this.siteIdValue = parseInt(btn.dataset.siteId)

    // Position editor over the <main> content area
    this._mainEl = this.element.closest("main")
    const rect = this._mainEl.getBoundingClientRect()
    const editor = this.editorTarget
    editor.style.position = "fixed"
    editor.style.top = `${rect.top}px`
    editor.style.left = `${rect.left}px`
    editor.style.width = `${rect.width}px`
    editor.style.height = `${rect.height}px`
    editor.style.zIndex = "40"
    editor.classList.remove("hidden")

    this._originalOverflow = this._mainEl.style.overflow
    this._mainEl.style.overflow = "hidden"

    // Recalculate on resize
    this._resizeHandler = () => {
      const r = this._mainEl.getBoundingClientRect()
      editor.style.top = `${r.top}px`
      editor.style.left = `${r.left}px`
      editor.style.width = `${r.width}px`
      editor.style.height = `${r.height}px`
    }
    window.addEventListener("resize", this._resizeHandler)

    this.fileTypeValue = btn.dataset.fileType || "pdf"

    if (this.hasFilenameTarget) this.filenameTarget.textContent = btn.dataset.filename || "PDF"

    // Ctrl/Meta+scroll zoom
    this._wheelHandler = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
        if (e.deltaY < 0) {
          this.zoomIn()
        } else if (e.deltaY > 0) {
          this.zoomOut()
        }
      }
    }
    this.containerTarget.addEventListener("wheel", this._wheelHandler, {
      passive: false,
    })

    // Every session starts in view mode with a clean toolbar state
    this.activateTool("view")

    this.showLoading(true)
    if (this.fileTypeValue === "image") {
      this.loadImage()
    } else {
      this.loadPdf()
    }
  }

  close() {
    this.closeContextMenu()
    this._closeHistorySidebar()
    this.autoSave().then(() => {
      this.editorTarget.classList.add("hidden")
      this.resetEditorPosition()
      this.cleanup()
    })
  }

  resetEditorPosition() {
    const editor = this.editorTarget
    editor.style.position = ""
    editor.style.top = ""
    editor.style.left = ""
    editor.style.width = ""
    editor.style.height = ""
    editor.style.zIndex = ""

    if (this._mainEl) {
      this._mainEl.style.overflow = this._originalOverflow || ""
      this._mainEl = null
    }
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler)
      this._resizeHandler = null
    }
  }

  cleanup() {
    Object.values(this.stages).forEach((s) => s.destroy())
    this.stages = {}
    this.layers = {}
    this.undoStack = []
    this.selectedNode = null
    this.markerCounter = 0
    this._backgroundImage = null
    this.pdfDocument = null
    this.zoomLevel = 1.0
    this.baseScale = 1
    if (this.hasZoomLevelTarget) {
      this.zoomLevelTarget.textContent = "100%"
    }
    if (this._wheelHandler) {
      this.containerTarget.removeEventListener("wheel", this._wheelHandler)
      this._wheelHandler = null
    }
    this.cancelDragDraw()
    this.containerTarget.innerHTML = ""
    this._historyOpen = false
    if (this.hasHistorySidebarTarget) {
      this.historySidebarTarget.style.width = "0px"
    }
    if (this.hasHistoryContentTarget) {
      this.historyContentTarget.innerHTML = ""
    }
    this.resetEditorPosition()
  }

  // ──────────────────────────────────────────────────
  // Zoom
  // ──────────────────────────────────────────────────
  zoomIn() {
    if (this._isZooming || this.zoomLevel >= 3.0) return
    this.zoomLevel = Math.min(this.zoomLevel + 0.25, 3.0)
    this.applyZoom()
  }

  zoomOut() {
    if (this._isZooming || this.zoomLevel <= 0.25) return
    this.zoomLevel = Math.max(this.zoomLevel - 0.25, 0.25)
    this.applyZoom()
  }

  async applyZoom() {
    this._isZooming = true

    // Cancel any pending debounced save to prevent it from running
    // mid-render and serializing empty/partial layers
    clearTimeout(this.saveTimeout)

    // Serialize current annotations before re-rendering
    const annotationData = this.serializeAll()

    // Update zoom display
    if (this.hasZoomLevelTarget) {
      this.zoomLevelTarget.textContent = `${Math.round(this.zoomLevel * 100)}%`
    }

    // Cancel any in-progress interactions
    this.cancelDragDraw()
    this.deselectAll()

    try {
      // Re-render pages at new zoom level
      if (this.fileTypeValue === "image") {
        this.renderImagePage()
      } else {
        await this.renderPdfPages()
      }

      // Restore annotations from local data
      Object.entries(annotationData.pages || {}).forEach(([pageNum, pageData]) => {
        const layer = this.layers[parseInt(pageNum)]
        if (!layer || !pageData.annotations) return

        pageData.annotations.forEach((ann) =>
          this.deserializeAnnotation(ann, layer, parseInt(pageNum)),
        )
        layer.batchDraw()
      })
    } finally {
      this._isZooming = false
    }
  }

  // ──────────────────────────────────────────────────
  // PDF Loading (pdf.js via helpers/load_pdf.js)
  // ──────────────────────────────────────────────────
  async loadPdf() {
    try {
      const pdf = await openPdf(this.pdfUrlValue)
      this.pdfDocument = pdf

      // Compute base scale from first page
      const firstPage = await pdf.getPage(1)
      const baseViewport = firstPage.getViewport({ scale: 1 })
      const containerWidth = this.containerTarget.clientWidth - 32
      this.baseScale = containerWidth / baseViewport.width

      await this.renderPdfPages()
      await this.loadAnnotations()
      this.showLoading(false)
    } catch (err) {
      console.error("PDF load error:", err)
      this.showLoading(false)
    }
  }

  async renderPdfPages(_skipAnnotations = false) {
    const pdf = this.pdfDocument
    if (!pdf) return

    // Destroy existing stages
    Object.values(this.stages).forEach((s) => s.destroy())
    this.stages = {}
    this.layers = {}
    this.containerTarget.innerHTML = ""

    this.scale = this.baseScale * this.zoomLevel

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i)
      const baseViewport = page.getViewport({ scale: 1 })
      const viewport = page.getViewport({ scale: this.scale })

      // Store PDF coordinate viewport for serialization
      this.pageViewports[i] = {
        width: baseViewport.width,
        height: baseViewport.height,
      }

      // Page wrapper
      const wrapper = document.createElement("div")
      wrapper.className = "relative mx-auto mb-4 shadow-lg"
      wrapper.style.width = `${viewport.width}px`
      wrapper.style.height = `${viewport.height}px`
      wrapper.dataset.page = i

      // Page label
      const label = document.createElement("div")
      label.className =
        "absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded z-10"
      label.textContent = `${this.i18nPageValue} ${i}`
      wrapper.appendChild(label)

      // PDF canvas
      const canvas = document.createElement("canvas")
      canvas.width = viewport.width
      canvas.height = viewport.height
      canvas.className = "block"
      wrapper.appendChild(canvas)

      // Konva container
      const konvaDiv = document.createElement("div")
      konvaDiv.className = "absolute inset-0"
      konvaDiv.id = `konva-page-${i}`
      wrapper.appendChild(konvaDiv)

      this.containerTarget.appendChild(wrapper)

      // Render PDF page
      await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise

      // Create Konva stage
      const stage = new Konva.Stage({
        container: `konva-page-${i}`,
        width: viewport.width,
        height: viewport.height,
      })
      const layer = new Konva.Layer()
      stage.add(layer)

      this.stages[i] = stage
      this.layers[i] = layer

      // Event handlers
      this.setupStageEvents(stage, layer, i)
    }
  }

  // ──────────────────────────────────────────────────
  // Image Loading
  // ──────────────────────────────────────────────────
  async loadImage() {
    try {
      const img = new Image()
      img.crossOrigin = "anonymous"

      await new Promise((resolve, reject) => {
        img.onload = resolve
        img.onerror = reject
        img.src = this.pdfUrlValue
      })

      this._backgroundImage = img

      const containerWidth = this.containerTarget.clientWidth - 32
      this.baseScale = containerWidth / img.naturalWidth

      this.renderImagePage()
      await this.loadAnnotations()
      this.showLoading(false)
    } catch (err) {
      console.error("Image load error:", err)
      this.showLoading(false)
    }
  }

  renderImagePage() {
    const img = this._backgroundImage
    if (!img) return

    // Destroy existing stages
    Object.values(this.stages).forEach((s) => s.destroy())
    this.stages = {}
    this.layers = {}
    this.containerTarget.innerHTML = ""

    this.scale = this.baseScale * this.zoomLevel
    const displayWidth = img.naturalWidth * this.scale
    const displayHeight = img.naturalHeight * this.scale

    // Store viewport for serialization (like PDF pages)
    this.pageViewports[1] = {
      width: img.naturalWidth,
      height: img.naturalHeight,
    }

    // Page wrapper
    const wrapper = document.createElement("div")
    wrapper.className = "relative mx-auto mb-4 shadow-lg"
    wrapper.style.width = `${displayWidth}px`
    wrapper.style.height = `${displayHeight}px`
    wrapper.dataset.page = 1

    // Image element as background
    const imgEl = document.createElement("img")
    imgEl.src = this.pdfUrlValue
    imgEl.className = "block"
    imgEl.style.width = `${displayWidth}px`
    imgEl.style.height = `${displayHeight}px`
    wrapper.appendChild(imgEl)

    // Konva container
    const konvaDiv = document.createElement("div")
    konvaDiv.className = "absolute inset-0"
    konvaDiv.id = "konva-page-1"
    wrapper.appendChild(konvaDiv)

    this.containerTarget.appendChild(wrapper)

    // Create Konva stage
    const stage = new Konva.Stage({
      container: "konva-page-1",
      width: displayWidth,
      height: displayHeight,
    })
    const layer = new Konva.Layer()
    stage.add(layer)

    this.stages[1] = stage
    this.layers[1] = layer

    this.setupStageEvents(stage, layer, 1)
  }

  // ──────────────────────────────────────────────────
  // Annotation Loading / Saving
  // ──────────────────────────────────────────────────
  async loadAnnotations() {
    try {
      const resp = await fetch(this.annotationUrlValue, {
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken },
      })
      if (!resp.ok) return

      const data = await resp.json()
      if (!data.annotation_data || !data.annotation_data.pages) return

      Object.entries(data.annotation_data.pages).forEach(([pageNum, pageData]) => {
        const layer = this.layers[parseInt(pageNum)]
        if (!layer || !pageData.annotations) return

        pageData.annotations.forEach((ann) =>
          this.deserializeAnnotation(ann, layer, parseInt(pageNum)),
        )
        layer.batchDraw()
      })
    } catch (err) {
      console.error("Load annotations error:", err)
    }
  }

  async save() {
    this.updateSaveStatus("saving")
    const annotationData = this.serializeAll()

    try {
      const resp = await fetch(this.annotationUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({
          pdf_annotation: {
            annotation_data: annotationData,
            page_count: Object.keys(this.stages).length,
          },
        }),
      })

      if (resp.ok) {
        this.updateSaveStatus("saved")
      } else {
        this.updateSaveStatus("error")
      }
    } catch (err) {
      console.error("Save error:", err)
      this.updateSaveStatus("error")
    }
  }

  autoSave() {
    return this.save()
  }

  debouncedSave() {
    clearTimeout(this.saveTimeout)
    this.saveTimeout = setTimeout(() => this.save(), 2000)
  }

  // ──────────────────────────────────────────────────
  // Serialization
  // ──────────────────────────────────────────────────
  serializeAll() {
    const pages = {}

    Object.entries(this.layers).forEach(([pageNum, layer]) => {
      const annotations = []
      const groups = layer.find("Group")

      groups.forEach((group) => {
        const ann = this.serializeNode(group, parseInt(pageNum))
        if (ann) annotations.push(ann)
      })

      // Also serialize ungrouped text nodes
      layer.find("Text").forEach((textNode) => {
        if (!textNode.parent || textNode.parent === layer) {
          const ann = this.serializeTextNode(textNode, parseInt(pageNum))
          if (ann) annotations.push(ann)
        }
      })

      if (annotations.length > 0 || this.pageViewports[pageNum]) {
        pages[pageNum] = {
          viewport: this.pageViewports[pageNum] || { width: 612, height: 792 },
          annotations,
        }
      }
    })

    return { version: 1, pages }
  }

  serializeNode(group, _pageNum) {
    const type = group.getAttr("annotationType")
    if (!type) return null

    const s = this.scale
    const base = { id: group.getAttr("annotationId"), type }

    switch (type) {
      case "marker": {
        const circle = group.findOne("Circle")
        const label = group.findOne(".markerLabel")
        const textNode = group.findOne(".markerText")
        return {
          ...base,
          x: group.x() / s,
          y: group.y() / s,
          color: circle ? circle.fill() : this.currentColor,
          label: label ? label.text() : "",
          text: textNode ? textNode.text() : "",
        }
      }
      case "arrow": {
        const arrow = group.findOne("Arrow")
        const textNode = group.findOne("Text")
        return {
          ...base,
          points: arrow ? arrow.points().map((p) => p / s) : [],
          color: arrow ? arrow.stroke() : this.currentColor,
          stroke_width: arrow ? arrow.strokeWidth() / s : 3,
          text: textNode ? textNode.text() : "",
          text_position: textNode ? { x: textNode.x() / s, y: textNode.y() / s } : null,
        }
      }
      case "line": {
        const line = group.findOne("Line")
        const textNode = group.findOne("Text")
        return {
          ...base,
          points: line ? line.points().map((p) => p / s) : [],
          color: line ? line.stroke() : this.currentColor,
          stroke_width: line ? line.strokeWidth() / s : 2,
          text: textNode ? textNode.text() : "",
        }
      }
      case "freehand": {
        const fhLine = group.findOne("Line")
        return {
          ...base,
          points: fhLine ? fhLine.points().map((p) => p / s) : [],
          color: fhLine ? fhLine.stroke() : this.currentColor,
          stroke_width: fhLine ? fhLine.strokeWidth() / s : 3,
        }
      }
      case "rect": {
        const rectShape = group.findOne("Rect")
        return {
          ...base,
          x: group.x() / s,
          y: group.y() / s,
          width: rectShape.width() / s,
          height: rectShape.height() / s,
          color: rectShape.stroke(),
          stroke_width: rectShape.strokeWidth() / s,
          filled: rectShape.fill() !== "transparent",
        }
      }
      case "ellipse": {
        const ellipseShape = group.findOne("Ellipse")
        return {
          ...base,
          x: group.x() / s,
          y: group.y() / s,
          width: (ellipseShape.radiusX() * 2) / s,
          height: (ellipseShape.radiusY() * 2) / s,
          color: ellipseShape.stroke(),
          stroke_width: ellipseShape.strokeWidth() / s,
          filled: ellipseShape.fill() !== "transparent",
        }
      }
      case "checkmark": {
        const checkLine = group.findOne("Line")
        return {
          ...base,
          x: group.x() / s,
          y: group.y() / s,
          color: checkLine ? checkLine.stroke() : this.currentColor,
          size: group.getAttr("checkmarkSize") || 1.0,
        }
      }
      case "xmark": {
        const xLine = group.findOne("Line")
        return {
          ...base,
          x: group.x() / s,
          y: group.y() / s,
          color: xLine ? xLine.stroke() : this.currentColor,
          size: group.getAttr("xmarkSize") || 1.0,
        }
      }
      case "task": {
        const rect = group.findOne("Rect")
        return {
          ...base,
          x: group.x() / s,
          y: group.y() / s,
          color: rect ? rect.fill() : "#FF6B00",
          task_id: group.getAttr("taskId"),
          task_name: group.getAttr("taskName"),
          task_url: group.getAttr("taskUrl"),
          task_status: group.getAttr("taskStatus"),
        }
      }
      default:
        return null
    }
  }

  serializeTextNode(node, _pageNum) {
    const s = this.scale
    return {
      id: node.getAttr("annotationId"),
      type: "text",
      x: node.x() / s,
      y: node.y() / s,
      content: node.text(),
      font_size: node.fontSize() / s,
      font_style: node.fontStyle(),
      color: node.fill(),
    }
  }

  // ──────────────────────────────────────────────────
  // Deserialization
  // ──────────────────────────────────────────────────
  deserializeAnnotation(ann, layer, _pageNum) {
    const s = this.scale

    switch (ann.type) {
      case "marker":
        this.createMarkerNode(layer, ann.x * s, ann.y * s, ann.color, ann.label, ann.text, ann.id)
        break
      case "arrow":
        this.createArrowNode(
          layer,
          ann.points.map((p) => p * s),
          ann.color,
          (ann.stroke_width || 3) * s,
          ann.text,
          ann.text_position ? { x: ann.text_position.x * s, y: ann.text_position.y * s } : null,
          ann.id,
        )
        break
      case "line":
        this.createLineNode(
          layer,
          ann.points.map((p) => p * s),
          ann.color,
          (ann.stroke_width || 2) * s,
          ann.text,
          ann.id,
        )
        break
      case "freehand":
        this.createFreehandNode(
          layer,
          ann.points.map((p) => p * s),
          ann.color,
          (ann.stroke_width || 3) * s,
          ann.id,
        )
        break
      case "rect":
        this.createRectNode(
          layer,
          ann.x * s,
          ann.y * s,
          ann.width * s,
          ann.height * s,
          ann.color,
          (ann.stroke_width || 2) * s,
          ann.filled || false,
          ann.id,
        )
        break
      case "ellipse":
        this.createEllipseNode(
          layer,
          ann.x * s,
          ann.y * s,
          ann.width * s,
          ann.height * s,
          ann.color,
          (ann.stroke_width || 2) * s,
          ann.filled || false,
          ann.id,
        )
        break
      case "text":
        this.createTextNode(
          layer,
          ann.x * s,
          ann.y * s,
          ann.content,
          (ann.font_size || 16) * s,
          ann.font_style || "normal",
          ann.color,
          ann.id,
        )
        break
      case "checkmark":
        this.createCheckmarkNode(layer, ann.x * s, ann.y * s, ann.color, ann.size || 1.0, ann.id)
        break
      case "xmark":
        this.createXmarkNode(layer, ann.x * s, ann.y * s, ann.color, ann.size || 1.0, ann.id)
        break
      case "task":
        this.createTaskMarkerNode(
          layer,
          ann.x * s,
          ann.y * s,
          ann.task_id,
          ann.task_name,
          ann.task_url,
          ann.id,
          ann.color,
          ann.task_status,
        )
        break
    }
  }

  // ──────────────────────────────────────────────────
  // Stage Events
  // ──────────────────────────────────────────────────
  setupStageEvents(stage, layer, pageNum) {
    // Click for marker, text, and select
    stage.on("click tap", (e) => {
      // Ignore clicks that ended a drag-draw
      if (this.isDragDrawing) return

      const pos = stage.getPointerPosition()
      if (!pos) return

      switch (this.currentTool) {
        case "marker":
          this.placeMarker(layer, pos.x, pos.y)
          return
        case "text":
          this.placeText(layer, pos.x, pos.y)
          return
        case "checkmark":
          this.placeCheckmark(layer, pos.x, pos.y)
          return
        case "xmark":
          this.placeXmark(layer, pos.x, pos.y)
          return
        case "task":
          this.placeTaskMarker(layer, pos.x, pos.y, pageNum)
          return
        case "arrow":
        case "line":
        case "freehand":
        case "rect":
        case "ellipse":
          // Handled by mousedown/mousemove/mouseup below
          return
        case "view":
          this.handleViewClick(e, stage)
          return
        case "select":
          if (e.target === stage) {
            this.deselectAll()
            return
          }
          this.handleSelect(e)
          break
      }
    })

    // Right-click context menu
    stage.on("contextmenu", (e) => {
      e.evt.preventDefault()
      const pos = stage.getPointerPosition()
      if (!pos) return
      this.showContextMenu(e.evt.clientX, e.evt.clientY, layer, pos.x, pos.y, pageNum)
    })

    // Drag-draw for arrow, line, and freehand
    stage.on("mousedown touchstart", (_e) => {
      if (
        this.currentTool !== "arrow" &&
        this.currentTool !== "line" &&
        this.currentTool !== "freehand" &&
        this.currentTool !== "rect" &&
        this.currentTool !== "ellipse"
      )
        return

      const pos = stage.getPointerPosition()
      if (!pos) return

      this.isDragDrawing = false
      this.dragDrawStart = pos
      this.dragDrawLayer = layer
      this.dragDrawPageNum = pageNum

      // Freehand: create preview line that accumulates points
      if (this.currentTool === "freehand") {
        this.dragPreview = new Konva.Line({
          points: [pos.x, pos.y],
          stroke: this.currentColor,
          strokeWidth: 3 * this.scale,
          lineCap: "round",
          lineJoin: "round",
          name: "dragPreview",
          listening: false,
        })
        layer.add(this.dragPreview)
        layer.batchDraw()
        return
      }

      // Create a temporary preview shape
      if (this.currentTool === "arrow") {
        this.dragPreview = new Konva.Arrow({
          points: [pos.x, pos.y, pos.x, pos.y],
          stroke: this.currentColor,
          fill: this.currentColor,
          strokeWidth: 3 * this.scale,
          pointerLength: 10 * this.scale,
          pointerWidth: 8 * this.scale,
          name: "dragPreview",
          listening: false,
        })
      } else if (this.currentTool === "rect") {
        this.dragPreview = new Konva.Rect({
          x: pos.x,
          y: pos.y,
          width: 0,
          height: 0,
          stroke: this.currentColor,
          strokeWidth: 2 * this.scale,
          fill: this.fillEnabled ? this.currentColor : "transparent",
          opacity: this.fillEnabled ? 0.3 : 1,
          dash: [10, 5],
          name: "dragPreview",
          listening: false,
        })
      } else if (this.currentTool === "ellipse") {
        this.dragPreview = new Konva.Ellipse({
          x: pos.x,
          y: pos.y,
          radiusX: 0,
          radiusY: 0,
          stroke: this.currentColor,
          strokeWidth: 2 * this.scale,
          fill: this.fillEnabled ? this.currentColor : "transparent",
          opacity: this.fillEnabled ? 0.3 : 1,
          dash: [10, 5],
          name: "dragPreview",
          listening: false,
        })
      } else {
        this.dragPreview = new Konva.Line({
          points: [pos.x, pos.y, pos.x, pos.y],
          stroke: this.currentColor,
          strokeWidth: 2 * this.scale,
          dash: [10, 5],
          name: "dragPreview",
          listening: false,
        })
      }
      layer.add(this.dragPreview)
      layer.batchDraw()
    })

    stage.on("mousemove touchmove", () => {
      if (!this.dragPreview || !this.dragDrawStart) return

      const pos = stage.getPointerPosition()
      if (!pos) return

      this.isDragDrawing = true

      if (this.currentTool === "freehand") {
        const newPoints = this.dragPreview.points().concat([pos.x, pos.y])
        this.dragPreview.points(newPoints)
      } else if (this.currentTool === "rect") {
        const start = this.dragDrawStart
        this.dragPreview.x(Math.min(start.x, pos.x))
        this.dragPreview.y(Math.min(start.y, pos.y))
        this.dragPreview.width(Math.abs(pos.x - start.x))
        this.dragPreview.height(Math.abs(pos.y - start.y))
      } else if (this.currentTool === "ellipse") {
        const start = this.dragDrawStart
        this.dragPreview.x((start.x + pos.x) / 2)
        this.dragPreview.y((start.y + pos.y) / 2)
        this.dragPreview.radiusX(Math.abs(pos.x - start.x) / 2)
        this.dragPreview.radiusY(Math.abs(pos.y - start.y) / 2)
      } else {
        const start = this.dragDrawStart
        this.dragPreview.points([start.x, start.y, pos.x, pos.y])
      }
      this.dragDrawLayer.batchDraw()
    })

    stage.on("mouseup touchend", () => {
      if (!this.dragPreview || !this.dragDrawStart) return

      const pos = stage.getPointerPosition()
      if (!pos) {
        this.cancelDragDraw()
        return
      }

      // Freehand finalization
      if (this.currentTool === "freehand") {
        const points = this.dragPreview.points()
        this.dragPreview.destroy()
        this.dragPreview = null

        if (points.length >= 4) {
          const group = this.createFreehandNode(layer, points, this.currentColor, 3 * this.scale)
          this.pushUndo({ action: "add", node: group, layer })
          layer.batchDraw()
          this.debouncedSave()
        } else {
          layer.batchDraw()
        }

        this.dragDrawStart = null
        this.dragDrawLayer = null
        setTimeout(() => {
          this.isDragDrawing = false
        }, 50)
        return
      }

      const start = this.dragDrawStart
      const dist = Math.sqrt(Math.pow(pos.x - start.x, 2) + Math.pow(pos.y - start.y, 2))

      // Remove preview
      this.dragPreview.destroy()
      this.dragPreview = null

      // Only create if dragged a minimum distance
      if (dist < 5) {
        this.dragDrawStart = null
        this.dragDrawLayer = null
        // Reset after a tick so the click handler ignores this
        setTimeout(() => {
          this.isDragDrawing = false
        }, 50)
        layer.batchDraw()
        return
      }

      let group
      if (this.currentTool === "rect") {
        const rx = Math.min(start.x, pos.x)
        const ry = Math.min(start.y, pos.y)
        const rw = Math.abs(pos.x - start.x)
        const rh = Math.abs(pos.y - start.y)
        group = this.createRectNode(
          layer,
          rx,
          ry,
          rw,
          rh,
          this.currentColor,
          2 * this.scale,
          this.fillEnabled,
        )
      } else if (this.currentTool === "ellipse") {
        const ex = Math.min(start.x, pos.x)
        const ey = Math.min(start.y, pos.y)
        const ew = Math.abs(pos.x - start.x)
        const eh = Math.abs(pos.y - start.y)
        group = this.createEllipseNode(
          layer,
          ex,
          ey,
          ew,
          eh,
          this.currentColor,
          2 * this.scale,
          this.fillEnabled,
        )
      } else {
        const points = [start.x, start.y, pos.x, pos.y]
        if (this.currentTool === "arrow") {
          group = this.createArrowNode(layer, points, this.currentColor, 3 * this.scale, "", null)
        } else {
          group = this.createLineNode(layer, points, this.currentColor, 2 * this.scale, "")
        }
      }
      this.pushUndo({ action: "add", node: group, layer })
      layer.batchDraw()
      this.debouncedSave()

      this.dragDrawStart = null
      this.dragDrawLayer = null
      // Reset after a tick so the click handler ignores this
      setTimeout(() => {
        this.isDragDrawing = false
      }, 50)
    })
  }

  cancelDragDraw() {
    if (this.dragPreview) {
      this.dragPreview.destroy()
      this.dragPreview = null
    }
    this.dragDrawStart = null
    this.dragDrawLayer = null
    this.isDragDrawing = false
  }

  // ──────────────────────────────────────────────────
  // Context Menu
  // ──────────────────────────────────────────────────
  showContextMenu(clientX, clientY, layer, x, y, pageNum) {
    this.closeContextMenu()

    const menu = document.createElement("div")
    menu.id = "pdf-annotator-context-menu"
    menu.className =
      "fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-2 flex flex-col space-y-1"
    menu.style.left = `${clientX}px`
    menu.style.top = `${clientY}px`

    const commentBtn = document.createElement("button")
    commentBtn.dataset.contextAction = "comment"
    commentBtn.className =
      "flex items-center space-x-2 px-4 py-2 rounded-md bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors"
    commentBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zm-4 0H9v2h2V9z" clip-rule="evenodd" />
        </svg>
    `
    const commentSpan = document.createElement("span")
    commentSpan.textContent = this.i18nCommentValue
    commentBtn.appendChild(commentSpan)

    const taskBtn = document.createElement("button")
    taskBtn.dataset.contextAction = "task"
    taskBtn.className =
      "flex items-center space-x-2 px-4 py-2 rounded-md bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors"
    taskBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
        </svg>
    `
    const taskSpan = document.createElement("span")
    taskSpan.textContent = this.i18nCreateTaskValue
    taskBtn.appendChild(taskSpan)

    menu.appendChild(commentBtn)
    menu.appendChild(taskBtn)

    document.body.appendChild(menu)

    menu.querySelector('[data-context-action="comment"]').addEventListener("click", () => {
      this.closeContextMenu()
      this.placeText(layer, x, y)
    })
    menu.querySelector('[data-context-action="task"]').addEventListener("click", () => {
      this.closeContextMenu()
      this.placeTaskMarker(layer, x, y, pageNum)
    })

    // Close on outside click (next tick to avoid immediate dismiss)
    requestAnimationFrame(() => {
      this._closeContextMenuBound = () => this.closeContextMenu()
      document.addEventListener("click", this._closeContextMenuBound, {
        once: true,
      })
    })

    // Close on Escape
    this._closeContextMenuOnEscape = (e) => {
      if (e.key === "Escape") this.closeContextMenu()
    }
    document.addEventListener("keydown", this._closeContextMenuOnEscape, {
      once: true,
    })
  }

  closeContextMenu() {
    const menu = document.getElementById("pdf-annotator-context-menu")
    if (menu) menu.remove()

    if (this._closeContextMenuBound) {
      document.removeEventListener("click", this._closeContextMenuBound)
      this._closeContextMenuBound = null
    }
    if (this._closeContextMenuOnEscape) {
      document.removeEventListener("keydown", this._closeContextMenuOnEscape)
      this._closeContextMenuOnEscape = null
    }
  }

  // ──────────────────────────────────────────────────
  // Tool Selection
  // ──────────────────────────────────────────────────
  selectTool(event) {
    this.activateTool(event.currentTarget.dataset.tool)
  }

  selectMode(event) {
    const mode = event.currentTarget.dataset.mode
    this.activateTool(mode === "view" ? "view" : this._lastEditTool || "select")
  }

  selectGroupTool(event) {
    const groupEl = event.currentTarget.closest("[data-group]")
    this.activateTool(groupEl.dataset.currentTool)
  }

  toggleGroupFlyout(event) {
    event.stopPropagation()
    const flyout = event.currentTarget.closest("[data-group]").querySelector("[data-group-flyout]")
    const isOpen = !flyout.classList.contains("hidden")
    this.closeGroupFlyouts()
    if (!isOpen) flyout.classList.remove("hidden")
  }

  closeGroupFlyouts() {
    if (!this.hasToolbarTarget) return
    this.toolbarTarget
      .querySelectorAll("[data-group-flyout]")
      .forEach((flyout) => flyout.classList.add("hidden"))
  }

  activateTool(tool) {
    this.currentTool = tool
    if (tool !== "view") this._lastEditTool = tool
    this.cancelDragDraw()
    this.deselectAll()
    this.closeContextMenu()
    this.closeGroupFlyouts()

    // Toggle draggable state on all annotations for view mode
    const isViewMode = tool === "view"
    Object.values(this.layers).forEach((layer) => {
      layer.find("Group").forEach((group) => {
        if (group.getAttr("annotationType")) {
          group.draggable(!isViewMode)
        }
      })
      layer.find("Text").forEach((text) => {
        if (text.getAttr("annotationType")) {
          text.draggable(!isViewMode)
        }
      })
      layer.batchDraw()
    })

    this.updateToolbarState(tool)

    // Update cursor
    Object.values(this.stages).forEach((stage) => {
      if (tool === "view" || tool === "select") {
        stage.container().style.cursor = "default"
      } else {
        stage.container().style.cursor = "crosshair"
      }
    })
  }

  updateToolbarState(tool) {
    const isViewMode = tool === "view"

    // Mode segment highlight
    this.modeButtonTargets.forEach((btn) => {
      const isActive = (btn.dataset.mode === "view") === isViewMode
      btn.classList.toggle("bg-green-100", isActive)
      btn.classList.toggle("text-green-700", isActive)
      btn.classList.toggle("text-gray-600", !isActive)
      btn.classList.toggle("hover:bg-gray-50", !isActive)
    })

    // Show edit-only zones only in edit mode
    this.editZoneTargets.forEach((zone) => {
      zone.classList.toggle("hidden", isViewMode)
      zone.classList.toggle("flex", !isViewMode)
    })

    // Active state for tool buttons (top-level and flyout entries)
    this.toolbarTarget.querySelectorAll("[data-edit-tools] [data-tool]").forEach((btn) => {
      const isActive = btn.dataset.tool === tool
      btn.classList.toggle("bg-green-100", isActive)
      btn.classList.toggle("text-green-700", isActive)
      if (!btn.closest("[data-group-flyout]")) {
        btn.classList.toggle("text-gray-600", !isActive)
      }
    })

    // Tool groups: remember last-used tool, swap the main button's icon, highlight when active
    this.toolGroupTargets.forEach((groupEl) => {
      const groupActive = TOOL_GROUPS[tool] === groupEl.dataset.group
      if (groupActive) groupEl.dataset.currentTool = tool

      const currentTool = groupEl.dataset.currentTool
      groupEl.querySelectorAll("[data-group-icon]").forEach((icon) => {
        icon.classList.toggle("hidden", icon.dataset.groupIcon !== currentTool)
      })

      const mainButton = groupEl.querySelector("[data-group-main]")
      mainButton.classList.toggle("bg-green-100", groupActive)
      mainButton.classList.toggle("text-green-700", groupActive)
      mainButton.classList.toggle("text-gray-600", !groupActive)
    })

    this.updateContextualControls()
  }

  updateContextualControls() {
    const selectedType = this.selectedNode ? this.selectedNode.getAttr("annotationType") : null
    const isViewMode = this.currentTool === "view"

    const fontVisible = !isViewMode && (this.currentTool === "text" || selectedType === "text")
    // Fill only applies to newly drawn shapes, so it is tied to the tool, not the selection
    const fillVisible = !isViewMode && ["rect", "ellipse"].includes(this.currentTool)

    if (this.hasFontControlsTarget) {
      this.fontControlsTarget.classList.toggle("hidden", !fontVisible)
      this.fontControlsTarget.classList.toggle("flex", fontVisible)
    }
    if (this.hasFillControlsTarget) {
      this.fillControlsTarget.classList.toggle("hidden", !fillVisible)
      this.fillControlsTarget.classList.toggle("flex", fillVisible)
    }
  }

  updateColor(event) {
    this.currentColor = event.currentTarget.value
    if (this.selectedNode) {
      this.applyColorToNode(this.selectedNode, this.currentColor)
      this.debouncedSave()
    }
  }

  selectQuickColor(event) {
    const color = event.currentTarget.dataset.color
    this.currentColor = color
    if (this.hasColorPickerTarget) this.colorPickerTarget.value = color
    if (this.selectedNode) {
      this.applyColorToNode(this.selectedNode, color)
      this.debouncedSave()
    }
  }

  updateFontSize(event) {
    this.currentFontSize = parseInt(event.currentTarget.value) || 16
    if (this.selectedNode && this.selectedNode.getAttr("annotationType") === "text") {
      const textNode = this.selectedNode
      textNode.fontSize(this.currentFontSize * this.scale)
      textNode.getLayer().batchDraw()
      this.debouncedSave()
    }
  }

  updateFontStyle(event) {
    this.currentFontStyle = event.currentTarget.value
    if (this.selectedNode && this.selectedNode.getAttr("annotationType") === "text") {
      const textNode = this.selectedNode
      textNode.fontStyle(this.currentFontStyle)
      textNode.getLayer().batchDraw()
      this.debouncedSave()
    }
  }

  toggleFill() {
    this.fillEnabled = !this.fillEnabled
    if (this.hasFillToggleTarget) {
      const btn = this.fillToggleTarget
      const svg = btn.querySelector("svg")
      if (this.fillEnabled) {
        btn.classList.add("bg-green-100", "text-green-700", "border-green-300")
        btn.classList.remove("text-gray-600", "border-gray-300")
        svg.setAttribute("fill", "currentColor")
      } else {
        btn.classList.remove("bg-green-100", "text-green-700", "border-green-300")
        btn.classList.add("text-gray-600", "border-gray-300")
        svg.setAttribute("fill", "none")
      }
    }
  }

  // ──────────────────────────────────────────────────
  // Annotation Creation
  // ──────────────────────────────────────────────────
  placeMarker(layer, x, y) {
    this.markerCounter++
    const label = String.fromCharCode(64 + ((this.markerCounter - 1) % 26) + 1) // A-Z
    const group = this.createMarkerNode(layer, x, y, this.currentColor, label, "")
    this.pushUndo({ action: "add", node: group, layer })
    layer.batchDraw()
    this.debouncedSave()
  }

  createMarkerNode(layer, x, y, color, label, text, id) {
    const r = 14 * this.scale
    const group = new Konva.Group({
      x,
      y,
      draggable: true,
      annotationType: "marker",
      annotationId: id || this.generateId(),
    })

    group.add(
      new Konva.Circle({
        radius: r,
        fill: color,
        stroke: "#fff",
        strokeWidth: 2,
      }),
    )

    group.add(
      new Konva.Text({
        text: label,
        fontSize: 12 * this.scale,
        fill: "#fff",
        fontStyle: "bold",
        name: "markerLabel",
        offsetX: 4 * this.scale,
        offsetY: 6 * this.scale,
      }),
    )

    if (text) {
      group.add(
        new Konva.Text({
          x: r + 4,
          y: -8 * this.scale,
          text,
          fontSize: 12 * this.scale,
          fill: color,
          name: "markerText",
          padding: 2,
        }),
      )
    }

    group.on("dragend", () => this.debouncedSave())
    group.on("dblclick dbltap", () => this.editMarkerText(group))
    layer.add(group)
    return group
  }

  placeCheckmark(layer, x, y) {
    const group = this.createCheckmarkNode(layer, x, y, this.currentColor, 1.0)
    this.pushUndo({ action: "add", node: group, layer })
    layer.batchDraw()
    this.debouncedSave()
  }

  createCheckmarkNode(layer, x, y, color, size, id) {
    const s = this.scale * (size || 1.0)
    const group = new Konva.Group({
      x,
      y,
      draggable: true,
      annotationType: "checkmark",
      annotationId: id || this.generateId(),
      checkmarkSize: size || 1.0,
    })

    group.add(
      new Konva.Line({
        points: [-7 * s, 0, -2 * s, 5 * s, 7 * s, -5 * s],
        stroke: color,
        strokeWidth: 3 * s,
        lineCap: "round",
        lineJoin: "round",
        name: "checkmarkLine",
      }),
    )

    group.on("dragend", () => this.debouncedSave())
    layer.add(group)
    return group
  }

  placeXmark(layer, x, y) {
    const group = this.createXmarkNode(layer, x, y, this.currentColor, 1.0)
    this.pushUndo({ action: "add", node: group, layer })
    layer.batchDraw()
    this.debouncedSave()
  }

  createXmarkNode(layer, x, y, color, size, id) {
    const s = this.scale * (size || 1.0)
    const group = new Konva.Group({
      x,
      y,
      draggable: true,
      annotationType: "xmark",
      annotationId: id || this.generateId(),
      xmarkSize: size || 1.0,
    })

    ;[
      [-6 * s, -6 * s, 6 * s, 6 * s],
      [-6 * s, 6 * s, 6 * s, -6 * s],
    ].forEach((points) => {
      group.add(
        new Konva.Line({
          points,
          stroke: color,
          strokeWidth: 3 * s,
          lineCap: "round",
          lineJoin: "round",
          name: "xmarkLine",
        }),
      )
    })

    group.on("dragend", () => this.debouncedSave())
    layer.add(group)
    return group
  }

  editMarkerText(group) {
    let textNode = group.findOne(".markerText")
    const existingText = textNode ? textNode.text() : ""
    const newText = prompt(this.i18nTextPromptValue, existingText)
    if (newText === null) return

    if (textNode) {
      textNode.text(newText)
    } else if (newText) {
      const r = 14 * this.scale
      textNode = new Konva.Text({
        x: r + 4,
        y: -8 * this.scale,
        text: newText,
        fontSize: 12 * this.scale,
        fill: group.findOne("Circle").fill(),
        name: "markerText",
        padding: 2,
      })
      group.add(textNode)
    }
    group.getLayer().batchDraw()
    this.debouncedSave()
  }

  placeTaskMarker(layer, x, y, pageNum) {
    if (!this.siteIdValue) {
      alert(this.i18nNoSiteValue)
      return
    }

    this.pendingTaskPosition = { layer, x, y, pageNum }

    // Create a pulsating placeholder at click position
    this.pendingPlaceholder = this.createPendingPlaceholderNode(layer, x, y)
    layer.batchDraw()

    const csrfToken = this.csrfToken
    fetch(`/tasks/new?site_id=${this.siteIdValue}&source=pdf_annotation`, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html)

        // Defer observer setup so Turbo fully processes the stream first
        // (the stream removes any old offcanvas before appending the new one)
        requestAnimationFrame(() => {
          this._setupOffcanvasCloseWatch()
        })
      })
      .catch((error) => {
        console.error("Failed to open task form:", error)
        this.removePendingPlaceholder()
        this.pendingTaskPosition = null
      })
  }

  handleTaskCreated(event) {
    const { taskId, taskName, taskUrl, taskStatus } = event.detail
    if (!this.pendingTaskPosition) return

    // Remove placeholder before creating real marker
    if (this.pendingPlaceholder) {
      this.pendingPlaceholder.destroy()
      this.pendingPlaceholder = null
    }
    if (this._offcanvasAttrObserver) {
      this._offcanvasAttrObserver.disconnect()
      this._offcanvasAttrObserver = null
    }

    const { layer, x, y } = this.pendingTaskPosition
    const group = this.createTaskMarkerNode(
      layer,
      x,
      y,
      taskId,
      taskName,
      taskUrl,
      undefined,
      undefined,
      taskStatus,
    )
    this.pushUndo({ action: "add", node: group, layer })
    layer.batchDraw()
    this.debouncedSave()
    this.pendingTaskPosition = null
  }

  createPendingPlaceholderNode(layer, x, y) {
    const s = this.scale
    const color = this.currentColor
    const rectW = 28 * s
    const rectH = 28 * s

    const group = new Konva.Group({
      x,
      y,
      listening: false,
    })

    // Dashed-border rounded rectangle, semi-transparent
    group.add(
      new Konva.Rect({
        x: -rectW / 2,
        y: -rectH / 2,
        width: rectW,
        height: rectH,
        fill: color,
        opacity: 0.3,
        cornerRadius: 6 * s,
        stroke: color,
        strokeWidth: 2,
        dash: [4, 3],
      }),
    )

    // "+" indicator inside
    group.add(
      new Konva.Text({
        text: "+",
        fontSize: 18 * s,
        fill: color,
        fontStyle: "bold",
        align: "center",
        verticalAlign: "middle",
        width: rectW,
        height: rectH,
        offsetX: rectW / 2,
        offsetY: rectH / 2,
      }),
    )

    layer.add(group)

    // Pulsating animation: opacity oscillates 0.3 → 0.8
    const pulseIn = () => {
      if (!group.getParent()) return
      group.to({
        opacity: 0.3,
        duration: 0.8,
        onFinish: pulseOut,
      })
    }
    const pulseOut = () => {
      if (!group.getParent()) return
      group.to({
        opacity: 1,
        duration: 0.8,
        onFinish: pulseIn,
      })
    }
    pulseIn()

    return group
  }

  _setupOffcanvasCloseWatch() {
    const offcanvasEl = document.getElementById("offcanvas")
    if (!offcanvasEl) return

    // Watch for the offcanvas being hidden via X / Abbrechen.
    // The Stimulus controller sets data-offcanvas-open-value="false".
    // Save path is handled by handleTaskCreated (no observer needed).
    const observer = new MutationObserver(() => {
      if (offcanvasEl.dataset.offcanvasOpenValue === "false") {
        this.removePendingPlaceholder()
        this.pendingTaskPosition = null
        observer.disconnect()
        this._offcanvasAttrObserver = null
      }
    })
    observer.observe(offcanvasEl, {
      attributes: true,
      attributeFilter: ["data-offcanvas-open-value"],
    })
    this._offcanvasAttrObserver = observer

    // Guard against race: offcanvas already closed before observer was set up
    if (offcanvasEl.dataset.offcanvasOpenValue === "false") {
      this.removePendingPlaceholder()
      this.pendingTaskPosition = null
      observer.disconnect()
      this._offcanvasAttrObserver = null
    }
  }

  removePendingPlaceholder() {
    if (this.pendingPlaceholder) {
      const layer = this.pendingPlaceholder.getLayer()
      this.pendingPlaceholder.destroy()
      this.pendingPlaceholder = null
      if (layer) layer.batchDraw()
    }
    // Note: pendingTaskPosition is intentionally NOT cleared here.
    // The offcanvas removal (observed by MutationObserver) may fire
    // before handleTaskCreated on save, so the position must survive
    // for the real marker to be placed. It is cleared in
    // handleTaskCreated and on fetch error instead.
  }

  createTaskMarkerNode(layer, x, y, taskId, taskName, taskUrl, id, color, taskStatus) {
    const s = this.scale
    color = color || this.currentColor
    const group = new Konva.Group({
      x,
      y,
      draggable: true,
      annotationType: "task",
      annotationId: id || this.generateId(),
    })

    // Orange rounded rectangle background
    const rectW = 28 * s
    const rectH = 28 * s
    group.add(
      new Konva.Rect({
        x: -rectW / 2,
        y: -rectH / 2,
        width: rectW,
        height: rectH,
        fill: color,
        cornerRadius: 6 * s,
        stroke: "#fff",
        strokeWidth: 2,
      }),
    )

    // Checkmark icon
    group.add(
      new Konva.Line({
        points: [-5 * s, 1 * s, -1 * s, 5 * s, 7 * s, -5 * s],
        stroke: "#fff",
        strokeWidth: 2.5 * s,
        lineCap: "round",
        lineJoin: "round",
        name: "taskCheckmark",
      }),
    )

    // Task name label
    if (taskName) {
      group.add(
        new Konva.Text({
          x: rectW / 2 + 4,
          y: -8 * s,
          text: taskName,
          fontSize: 11 * s,
          fill: color,
          name: "taskLabel",
          padding: 2,
          fontStyle: "bold",
        }),
      )
    }

    // Task status label (second row, below task name)
    if (taskStatus) {
      group.add(
        new Konva.Text({
          x: rectW / 2 + 4,
          y: 4 * s,
          text: taskStatus,
          fontSize: 9 * s,
          fill: "#6B7280",
          name: "taskStatus",
          padding: 2,
          fontStyle: "normal",
        }),
      )
    }

    // Store task data as attributes
    group.setAttr("taskId", taskId)
    group.setAttr("taskName", taskName)
    group.setAttr("taskUrl", taskUrl)
    group.setAttr("taskStatus", taskStatus || "")

    group.on("dragend", () => this.debouncedSave())
    group.on("dblclick dbltap", () => {
      const url = group.getAttr("taskUrl")
      if (url) window.open(url, "_blank")
    })
    layer.add(group)
    return group
  }

  createArrowNode(layer, points, color, strokeWidth, text, textPosition, id) {
    const group = new Konva.Group({
      draggable: true,
      annotationType: "arrow",
      annotationId: id || this.generateId(),
    })

    group.add(
      new Konva.Arrow({
        points,
        stroke: color,
        fill: color,
        strokeWidth,
        pointerLength: 10 * this.scale,
        pointerWidth: 8 * this.scale,
      }),
    )

    if (text) {
      const tp = textPosition || {
        x: (points[0] + points[2]) / 2,
        y: (points[1] + points[3]) / 2 - 20 * this.scale,
      }
      group.add(
        new Konva.Text({
          x: tp.x,
          y: tp.y,
          text,
          fontSize: 12 * this.scale,
          fill: color,
          padding: 2,
        }),
      )
    }

    group.on("dragend", () => this.debouncedSave())
    group.on("dblclick dbltap", () => this.editGroupText(group))
    layer.add(group)
    return group
  }

  createLineNode(layer, points, color, strokeWidth, text, id) {
    const group = new Konva.Group({
      draggable: true,
      annotationType: "line",
      annotationId: id || this.generateId(),
    })

    group.add(
      new Konva.Line({
        points,
        stroke: color,
        strokeWidth,
        dash: [10, 5],
      }),
    )

    if (text) {
      const midX = (points[0] + points[2]) / 2
      const midY = (points[1] + points[3]) / 2 - 16 * this.scale
      group.add(
        new Konva.Text({
          x: midX,
          y: midY,
          text,
          fontSize: 12 * this.scale,
          fill: color,
          padding: 2,
        }),
      )
    }

    group.on("dragend", () => this.debouncedSave())
    group.on("dblclick dbltap", () => this.editGroupText(group))
    layer.add(group)
    return group
  }

  createFreehandNode(layer, points, color, strokeWidth, id) {
    const group = new Konva.Group({
      draggable: true,
      annotationType: "freehand",
      annotationId: id || this.generateId(),
    })

    group.add(
      new Konva.Line({
        points,
        stroke: color,
        strokeWidth,
        lineCap: "round",
        lineJoin: "round",
      }),
    )

    group.on("dragend", () => this.debouncedSave())
    layer.add(group)
    return group
  }

  createRectNode(layer, x, y, width, height, color, strokeWidth, filled, id) {
    const group = new Konva.Group({
      x,
      y,
      draggable: true,
      annotationType: "rect",
      annotationId: id || this.generateId(),
    })

    group.add(
      new Konva.Rect({
        x: 0,
        y: 0,
        width,
        height,
        stroke: color,
        strokeWidth,
        fill: filled ? color : "transparent",
        opacity: filled ? 0.3 : 1,
      }),
    )

    group.on("dragend", () => this.debouncedSave())
    layer.add(group)
    return group
  }

  createEllipseNode(layer, x, y, width, height, color, strokeWidth, filled, id) {
    const group = new Konva.Group({
      x,
      y,
      draggable: true,
      annotationType: "ellipse",
      annotationId: id || this.generateId(),
    })

    group.add(
      new Konva.Ellipse({
        x: width / 2,
        y: height / 2,
        radiusX: width / 2,
        radiusY: height / 2,
        stroke: color,
        strokeWidth,
        fill: filled ? color : "transparent",
        opacity: filled ? 0.3 : 1,
      }),
    )

    group.on("dragend", () => this.debouncedSave())
    layer.add(group)
    return group
  }

  editGroupText(group) {
    const textNode = group.findOne("Text")
    const existingText = textNode ? textNode.text() : ""
    const newText = prompt(this.i18nTextPromptValue, existingText)
    if (newText === null) return

    if (textNode) {
      textNode.text(newText)
    } else if (newText) {
      group.add(
        new Konva.Text({
          x: 0,
          y: -16 * this.scale,
          text: newText,
          fontSize: 12 * this.scale,
          fill: this.currentColor,
          padding: 2,
        }),
      )
    }
    group.getLayer().batchDraw()
    this.debouncedSave()
  }

  placeText(layer, x, y) {
    const node = this.createTextNode(
      layer,
      x,
      y,
      "Text",
      this.currentFontSize * this.scale,
      this.currentFontStyle,
      this.currentColor,
    )
    this.pushUndo({ action: "add", node, layer })
    layer.batchDraw()
    this.debouncedSave()

    // Immediately allow editing
    this.editTextInPlace(node)
  }

  createTextNode(layer, x, y, content, fontSize, fontStyle, color, id) {
    const node = new Konva.Text({
      x,
      y,
      text: content,
      fontSize,
      fontStyle,
      fill: color,
      draggable: true,
      padding: 4,
      annotationType: "text",
      annotationId: id || this.generateId(),
    })

    node.on("dragend", () => this.debouncedSave())
    node.on("dblclick dbltap", () => this.editTextInPlace(node))
    layer.add(node)
    return node
  }

  editTextInPlace(textNode) {
    const stage = textNode.getStage()
    const stageContainer = stage.container()
    const textPos = textNode.getClientRect()

    // Hide the Konva text while editing
    textNode.visible(false)
    textNode.getLayer().batchDraw()

    const textarea = document.createElement("textarea")
    textarea.value = textNode.text()
    textarea.style.position = "absolute"
    // Position relative to the stage container's parent (the page wrapper)
    const wrapper = stageContainer.parentElement
    textarea.style.top = `${textPos.y}px`
    textarea.style.left = `${textPos.x}px`
    textarea.style.width = `${Math.max(textPos.width + 40, 150)}px`
    textarea.style.height = `${Math.max(textPos.height + 20, 40)}px`
    textarea.style.fontSize = `${textNode.fontSize()}px`
    textarea.style.fontFamily = "sans-serif"
    textarea.style.color = textNode.fill()
    textarea.style.border = "2px solid #3B82F6"
    textarea.style.borderRadius = "4px"
    textarea.style.padding = "4px"
    textarea.style.zIndex = "100"
    textarea.style.background = "rgba(255,255,255,0.95)"
    textarea.style.outline = "none"
    textarea.style.resize = "both"
    textarea.style.lineHeight = "1.2"

    // Append inside the page wrapper (which is position:relative) so it sits on top of the Konva stage
    wrapper.appendChild(textarea)

    // Use requestAnimationFrame to ensure the element is rendered before focusing
    requestAnimationFrame(() => {
      textarea.focus()
      textarea.select()
    })

    let finished = false
    const finish = () => {
      if (finished) return
      finished = true
      const newText = textarea.value.trim()
      if (newText) {
        textNode.text(newText)
        textNode.visible(true)
      } else {
        // If empty, remove the text node
        textNode.destroy()
      }
      textarea.remove()
      const layer = textNode.getLayer()
      if (layer) layer.batchDraw()
      this.debouncedSave()
    }

    textarea.addEventListener("blur", finish)
    textarea.addEventListener("keydown", (e) => {
      // Stop keydown from propagating to the editor's handleKeydown
      e.stopPropagation()

      if (e.key === "Escape") {
        // Cancel — restore original text
        textarea.value = textNode.text()
        textNode.visible(true)
        textNode.getLayer().batchDraw()
        finished = true
        textarea.remove()
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        textarea.blur()
      }
    })
  }

  // ──────────────────────────────────────────────────
  // Selection
  // ──────────────────────────────────────────────────
  handleSelect(e) {
    const target = e.target
    const node =
      target.parent instanceof Konva.Group && target.parent.getAttr("annotationType")
        ? target.parent
        : target.getAttr("annotationType")
          ? target
          : null

    if (!node) {
      this.deselectAll()
      return
    }

    this.deselectAll()
    this.selectedNode = node

    // Add transformer
    const layer = node.getLayer()
    this.transformer = new Konva.Transformer({
      nodes: [node],
      enabledAnchors: ["top-left", "top-right", "bottom-left", "bottom-right"],
      boundBoxFunc: (oldBox, newBox) => newBox,
    })
    layer.add(this.transformer)
    layer.batchDraw()

    // Update UI to match selected node
    this.syncUIFromNode(node)
    this.updateContextualControls()
  }

  handleViewClick(e, _stage) {
    const target = e.target
    const node =
      target.parent instanceof Konva.Group && target.parent.getAttr("annotationType")
        ? target.parent
        : target.getAttr("annotationType")
          ? target
          : null

    if (!node) return

    const type = node.getAttr("annotationType")
    if (type === "task") {
      const taskId = node.getAttr("taskId")
      if (!taskId) return

      fetch(`/tasks/${taskId}?source=pdf_annotation`, {
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": this.csrfToken,
          "X-Requested-With": "XMLHttpRequest",
        },
      })
        .then((response) => response.text())
        .then((html) => Turbo.renderStreamMessage(html))
        .catch((error) => console.error("Failed to load task:", error))
    }
  }

  deselectAll() {
    if (this.transformer) {
      this.transformer.destroy()
      this.transformer = null
    }
    if (this.selectedNode) {
      const layer = this.selectedNode.getLayer()
      this.selectedNode = null
      if (layer) layer.batchDraw()
      this.updateContextualControls()
    }
  }

  syncUIFromNode(node) {
    const type = node.getAttr("annotationType")
    if (type === "marker") {
      const circle = node.findOne("Circle")
      if (circle && this.hasColorPickerTarget) this.colorPickerTarget.value = circle.fill()
    } else if (type === "arrow" || type === "line" || type === "freehand") {
      const shape = node.findOne("Arrow") || node.findOne("Line")
      if (shape && this.hasColorPickerTarget) this.colorPickerTarget.value = shape.stroke()
    } else if (type === "text") {
      if (this.hasColorPickerTarget) this.colorPickerTarget.value = node.fill()
      if (this.hasFontSizeInputTarget)
        this.fontSizeInputTarget.value = Math.round(node.fontSize() / this.scale)
      if (this.hasFontStyleSelectTarget) this.fontStyleSelectTarget.value = node.fontStyle()
    } else if (type === "rect") {
      const rectShape = node.findOne("Rect")
      if (rectShape && this.hasColorPickerTarget) this.colorPickerTarget.value = rectShape.stroke()
    } else if (type === "ellipse") {
      const ellipseShape = node.findOne("Ellipse")
      if (ellipseShape && this.hasColorPickerTarget)
        this.colorPickerTarget.value = ellipseShape.stroke()
    } else if (type === "checkmark" || type === "xmark") {
      const checkLine = node.findOne("Line")
      if (checkLine && this.hasColorPickerTarget) this.colorPickerTarget.value = checkLine.stroke()
    } else if (type === "task") {
      const rect = node.findOne("Rect")
      if (rect && this.hasColorPickerTarget) this.colorPickerTarget.value = rect.fill()
    }
  }

  applyColorToNode(node, color) {
    const type = node.getAttr("annotationType")
    if (type === "marker") {
      const circle = node.findOne("Circle")
      if (circle) circle.fill(color)
      const textNode = node.findOne(".markerText")
      if (textNode) textNode.fill(color)
    } else if (type === "arrow") {
      const arrow = node.findOne("Arrow")
      if (arrow) {
        arrow.stroke(color)
        arrow.fill(color)
      }
    } else if (type === "line" || type === "freehand") {
      const line = node.findOne("Line")
      if (line) line.stroke(color)
    } else if (type === "rect") {
      const rectShape = node.findOne("Rect")
      if (rectShape) {
        rectShape.stroke(color)
        if (rectShape.fill() !== "transparent") rectShape.fill(color)
      }
    } else if (type === "ellipse") {
      const ellipseShape = node.findOne("Ellipse")
      if (ellipseShape) {
        ellipseShape.stroke(color)
        if (ellipseShape.fill() !== "transparent") ellipseShape.fill(color)
      }
    } else if (type === "checkmark" || type === "xmark") {
      node.find("Line").forEach((line) => line.stroke(color))
    } else if (type === "text") {
      node.fill(color)
    } else if (type === "task") {
      const rect = node.findOne("Rect")
      if (rect) rect.fill(color)
      const taskLabel = node.findOne(".taskLabel")
      if (taskLabel) taskLabel.fill(color)
    }
    node.getLayer().batchDraw()
  }

  // ──────────────────────────────────────────────────
  // Undo / Delete
  // ──────────────────────────────────────────────────
  pushUndo(entry) {
    this.undoStack.push(entry)
  }

  undo() {
    if (this.undoStack.length === 0) return

    const entry = this.undoStack.pop()
    if (entry.action === "add") {
      entry.node.destroy()
      entry.layer.batchDraw()
    } else if (entry.action === "remove") {
      entry.layer.add(entry.node)
      entry.layer.batchDraw()
    }
    this.debouncedSave()
  }

  deleteSelected() {
    if (!this.selectedNode) return

    const layer = this.selectedNode.getLayer()
    const node = this.selectedNode
    this.pushUndo({ action: "remove", node, layer })
    this.deselectAll()
    node.destroy()
    layer.batchDraw()
    this.debouncedSave()
  }

  // Keyboard shortcuts
  handleKeydown(event) {
    if (event.key === "Delete" || event.key === "Backspace") {
      if (
        this.selectedNode &&
        document.activeElement.tagName !== "TEXTAREA" &&
        document.activeElement.tagName !== "INPUT"
      ) {
        event.preventDefault()
        this.deleteSelected()
      }
    }
    if ((event.ctrlKey || event.metaKey) && event.key === "z") {
      event.preventDefault()
      this.undo()
    }
    if (event.key === "Escape") {
      this.deselectAll()
      this.cancelDragDraw()
      this.closeGroupFlyouts()
      Object.values(this.layers).forEach((l) => l.batchDraw())
    }
  }

  // ──────────────────────────────────────────────────
  // History Sidebar
  // ──────────────────────────────────────────────────
  toggleHistory() {
    if (this._historyOpen) {
      this._closeHistorySidebar()
    } else {
      this._openHistorySidebar()
    }
  }

  _openHistorySidebar() {
    if (this._historyOpen) return

    fetch(this.annotationUrlValue + "/history", {
      headers: {
        Accept: "text/html",
        "X-CSRF-Token": this.csrfToken,
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        if (this.hasHistoryContentTarget) {
          this.historyContentTarget.innerHTML = html
        }
        this._showHistorySidebar()
      })
      .catch((error) => console.error("Failed to load history:", error))
  }

  _closeHistorySidebar() {
    if (!this._historyOpen) return
    this._hideHistorySidebar()
  }

  _showHistorySidebar() {
    if (!this.hasHistorySidebarTarget) return
    this._historyOpen = true
    this.historySidebarTarget.style.width = "384px"
    this._scheduleHistoryResize()
  }

  _hideHistorySidebar() {
    if (!this.hasHistorySidebarTarget) return
    this._historyOpen = false
    this.historySidebarTarget.style.width = "0px"
    this._scheduleHistoryResize()
  }

  _scheduleHistoryResize() {
    // Wait for CSS transition to complete (300ms) then re-render
    setTimeout(() => this._resizeAfterHistoryToggle(), 320)
  }

  async _resizeAfterHistoryToggle() {
    if (!this.hasContainerTarget) return

    // Recalculate baseScale from the (now wider or narrower) container
    const containerWidth = this.containerTarget.clientWidth - 32
    if (containerWidth <= 0) return

    if (this.fileTypeValue === "image" && this._backgroundImage) {
      this.baseScale = containerWidth / this._backgroundImage.naturalWidth
    } else if (this.pdfDocument) {
      const firstPage = await this.pdfDocument.getPage(1)
      const baseViewport = firstPage.getViewport({ scale: 1 })
      this.baseScale = containerWidth / baseViewport.width
    } else {
      return
    }

    // Re-render using the same pattern as applyZoom
    clearTimeout(this.saveTimeout)
    const annotationData = this.serializeAll()
    this.cancelDragDraw()
    this.deselectAll()

    try {
      if (this.fileTypeValue === "image") {
        this.renderImagePage()
      } else {
        await this.renderPdfPages()
      }

      Object.entries(annotationData.pages || {}).forEach(([pageNum, pageData]) => {
        const layer = this.layers[parseInt(pageNum)]
        if (!layer || !pageData.annotations) return
        pageData.annotations.forEach((ann) =>
          this.deserializeAnnotation(ann, layer, parseInt(pageNum)),
        )
        layer.batchDraw()
      })
    } catch (err) {
      console.error("History resize error:", err)
    }
  }

  // ──────────────────────────────────────────────────
  // History Highlight
  // ──────────────────────────────────────────────────
  handleHighlightRequest(event) {
    const { annotationId, actionType, page, x, y } = event.detail
    const pageNum = parseInt(page)
    if (!pageNum) return

    // Scroll the page wrapper into view within the overflow-auto container
    const wrapper = this.containerTarget.querySelector(`[data-page="${pageNum}"]`)
    if (wrapper) {
      wrapper.scrollIntoView({ behavior: "smooth", block: "start" })
    }

    // Small delay to let scroll complete before highlighting
    setTimeout(() => {
      if (actionType === "removed") {
        this.showGhostIndicator(pageNum, x, y)
      } else {
        this.highlightAnnotation(pageNum, annotationId)
      }
    }, 400)
  }

  highlightAnnotation(pageNum, annotationId) {
    if (!annotationId) return

    // Search on the expected page first, then fall back to all pages
    const pagesToSearch = [
      pageNum,
      ...Object.keys(this.layers)
        .map(Number)
        .filter((p) => p !== pageNum),
    ]

    for (const p of pagesToSearch) {
      const layer = this.layers[p]
      if (!layer) continue

      // Search Groups
      const groups = layer.find("Group")
      for (const group of groups) {
        if (group.getAttr("annotationId") === annotationId) {
          this.applyPulseHighlight(group, layer)
          return
        }
      }

      // Search standalone Text nodes
      const texts = layer.find("Text")
      for (const text of texts) {
        if (
          text.getAttr("annotationId") === annotationId &&
          (!text.parent || text.parent === layer)
        ) {
          this.applyPulseHighlight(text, layer)
          return
        }
      }
    }
  }

  applyPulseHighlight(node, layer) {
    const s = this.scale
    const padding = 6 * s
    const rect = node.getClientRect({ relativeTo: layer })

    const highlight = new Konva.Rect({
      x: rect.x - padding,
      y: rect.y - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
      stroke: "#3B82F6",
      strokeWidth: 2 * s,
      cornerRadius: 4 * s,
      dash: [6, 3],
      listening: false,
    })

    layer.add(highlight)
    layer.batchDraw()

    let pulseCount = 0
    const maxPulses = 3

    const pulseDown = () => {
      if (pulseCount >= maxPulses) {
        highlight.destroy()
        layer.batchDraw()
        return
      }
      highlight.to({
        opacity: 0.2,
        duration: 0.3,
        onFinish: pulseUp,
      })
    }

    const pulseUp = () => {
      pulseCount++
      highlight.to({
        opacity: 1,
        duration: 0.3,
        onFinish: pulseDown,
      })
    }

    pulseDown()
  }

  showGhostIndicator(pageNum, x, y) {
    const layer = this.layers[pageNum]
    if (!layer || (!x && !y)) return

    const s = this.scale
    const group = new Konva.Group({
      x: x * s,
      y: y * s,
      listening: false,
    })

    // Dashed red circle
    group.add(
      new Konva.Circle({
        radius: 16 * s,
        stroke: "#EF4444",
        strokeWidth: 2 * s,
        dash: [6, 4],
        fill: "transparent",
      }),
    )

    // Inner dot
    group.add(
      new Konva.Circle({
        radius: 3 * s,
        fill: "#EF4444",
      }),
    )

    layer.add(group)
    layer.batchDraw()

    // Pulse opacity 3 times then fade out and destroy
    let pulseCount = 0
    const maxPulses = 3

    const pulseDown = () => {
      if (pulseCount >= maxPulses) {
        // Fade out and destroy
        group.to({
          opacity: 0,
          duration: 0.4,
          onFinish: () => {
            group.destroy()
            layer.batchDraw()
          },
        })
        return
      }
      group.to({
        opacity: 0.3,
        duration: 0.3,
        onFinish: pulseUp,
      })
    }

    const pulseUp = () => {
      pulseCount++
      group.to({
        opacity: 1,
        duration: 0.3,
        onFinish: pulseDown,
      })
    }

    pulseDown()
  }

  // ──────────────────────────────────────────────────
  // Export (PDF / Image)
  // ──────────────────────────────────────────────────
  async download() {
    if (this.fileTypeValue === "image") {
      await this.downloadAsImage()
    } else {
      await this.downloadAsPdf()
    }
  }

  async downloadAsImage() {
    this.showLoading(true)

    try {
      await this.save()

      const bgImg = this._backgroundImage
      if (!bgImg) throw new Error("Background image not loaded")

      const canvas = document.createElement("canvas")
      canvas.width = bgImg.naturalWidth
      canvas.height = bgImg.naturalHeight
      const ctx = canvas.getContext("2d")

      // Draw original image at full resolution
      ctx.drawImage(bgImg, 0, 0)

      // Draw Konva layer on top, scaled back to original dimensions
      const stage = this.stages[1]
      if (stage) {
        const konvaCanvas = stage.toCanvas({ pixelRatio: 1 / this.scale })
        ctx.drawImage(konvaCanvas, 0, 0)
      }

      // Determine output type from filename
      const filename = this.getFilename()
      const isJpeg = /\.jpe?g$/i.test(filename)
      const mimeType = isJpeg ? "image/jpeg" : "image/png"

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = `annotated_${filename}`
        link.click()
        URL.revokeObjectURL(url)
      }, mimeType)
    } catch (err) {
      console.error("Image export error:", err)
    } finally {
      this.showLoading(false)
    }
  }

  async downloadAsPdf() {
    this.showLoading(true)

    try {
      await this.save()

      const {
        PDFDocument,
        StandardFonts,
        rgb,
        pushGraphicsState,
        popGraphicsState,
        setFillingColor,
        moveTo,
        lineTo,
        closePath,
        fill,
      } = await import("pdf-lib")

      const existingPdfBytes = await fetch(this.pdfUrlValue).then((r) => r.arrayBuffer())
      const pdfDoc = await PDFDocument.load(existingPdfBytes)
      const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica)
      const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)

      const annotationData = this.serializeAll()
      const pages = pdfDoc.getPages()

      Object.entries(annotationData.pages || {}).forEach(([pageNum, pageData]) => {
        const pageIndex = parseInt(pageNum) - 1
        if (pageIndex >= pages.length) return
        const page = pages[pageIndex]
        const { height: pageHeight } = page.getSize()

        ;(pageData.annotations || []).forEach((ann) => {
          const hexToRgb = (hex) => {
            const r = parseInt(hex.slice(1, 3), 16) / 255
            const g = parseInt(hex.slice(3, 5), 16) / 255
            const b = parseInt(hex.slice(5, 7), 16) / 255
            return rgb(r, g, b)
          }

          const color = hexToRgb(ann.color || "#FF0000")

          switch (ann.type) {
            case "marker": {
              // Draw filled circle
              page.drawCircle({
                x: ann.x,
                y: pageHeight - ann.y,
                size: 14,
                color,
                borderColor: rgb(1, 1, 1),
                borderWidth: 2,
              })
              // Draw label
              if (ann.label) {
                page.drawText(ann.label, {
                  x: ann.x - 4,
                  y: pageHeight - ann.y - 5,
                  size: 12,
                  font: helveticaBold,
                  color: rgb(1, 1, 1),
                })
              }
              // Draw text
              if (ann.text) {
                page.drawText(ann.text, {
                  x: ann.x + 18,
                  y: pageHeight - ann.y + 4,
                  size: 12,
                  font: helvetica,
                  color,
                })
              }
              break
            }
            case "arrow": {
              if (ann.points && ann.points.length >= 4) {
                const x1 = ann.points[0],
                  y1 = pageHeight - ann.points[1]
                const x2 = ann.points[2],
                  y2 = pageHeight - ann.points[3]
                const sw = ann.stroke_width || 3

                // Draw the line shaft
                page.drawLine({
                  start: { x: x1, y: y1 },
                  end: { x: x2, y: y2 },
                  thickness: sw,
                  color,
                })

                // Draw arrowhead as a filled triangle at the end point
                const angle = Math.atan2(y2 - y1, x2 - x1)
                const headLen = Math.max(sw * 4, 12)
                const headHalfW = headLen * 0.5
                // Two base corners of the triangle
                const bx1 = x2 - headLen * Math.cos(angle) + headHalfW * Math.sin(angle)
                const by1 = y2 - headLen * Math.sin(angle) - headHalfW * Math.cos(angle)
                const bx2 = x2 - headLen * Math.cos(angle) - headHalfW * Math.sin(angle)
                const by2 = y2 - headLen * Math.sin(angle) + headHalfW * Math.cos(angle)

                // Draw filled triangle using raw PDF operators (coords in page space, origin bottom-left)
                page.pushOperators(
                  pushGraphicsState(),
                  setFillingColor(color),
                  moveTo(x2, y2),
                  lineTo(bx1, by1),
                  lineTo(bx2, by2),
                  closePath(),
                  fill(),
                  popGraphicsState(),
                )

                if (ann.text) {
                  const midX = (ann.points[0] + ann.points[2]) / 2
                  const midY = (ann.points[1] + ann.points[3]) / 2
                  page.drawText(ann.text, {
                    x: midX,
                    y: pageHeight - midY + 10,
                    size: 12,
                    font: helvetica,
                    color,
                  })
                }
              }
              break
            }
            case "line": {
              if (ann.points && ann.points.length >= 4) {
                page.drawLine({
                  start: { x: ann.points[0], y: pageHeight - ann.points[1] },
                  end: { x: ann.points[2], y: pageHeight - ann.points[3] },
                  thickness: ann.stroke_width || 2,
                  color,
                  dashArray: [10, 5],
                })
                if (ann.text) {
                  const midX = (ann.points[0] + ann.points[2]) / 2
                  const midY = (ann.points[1] + ann.points[3]) / 2
                  page.drawText(ann.text, {
                    x: midX,
                    y: pageHeight - midY + 10,
                    size: 12,
                    font: helvetica,
                    color,
                  })
                }
              }
              break
            }
            case "freehand": {
              if (ann.points && ann.points.length >= 4) {
                for (let i = 0; i < ann.points.length - 2; i += 2) {
                  page.drawLine({
                    start: {
                      x: ann.points[i],
                      y: pageHeight - ann.points[i + 1],
                    },
                    end: {
                      x: ann.points[i + 2],
                      y: pageHeight - ann.points[i + 3],
                    },
                    thickness: ann.stroke_width || 3,
                    color,
                  })
                }
              }
              break
            }
            case "rect": {
              const rectOpts = {
                x: ann.x,
                y: pageHeight - ann.y - ann.height,
                width: ann.width,
                height: ann.height,
                borderColor: color,
                borderWidth: ann.stroke_width || 2,
              }
              if (ann.filled) {
                rectOpts.color = color
                rectOpts.opacity = 0.3
              }
              page.drawRectangle(rectOpts)
              break
            }
            case "ellipse": {
              const cx = ann.x + ann.width / 2
              const cy = pageHeight - ann.y - ann.height / 2
              const ellipseOpts = {
                x: cx,
                y: cy,
                xScale: ann.width / 2,
                yScale: ann.height / 2,
                borderColor: color,
                borderWidth: ann.stroke_width || 2,
              }
              if (ann.filled) {
                ellipseOpts.color = color
                ellipseOpts.opacity = 0.3
              }
              page.drawEllipse(ellipseOpts)
              break
            }
            case "text": {
              const font = ann.font_style === "bold" ? helveticaBold : helvetica
              page.drawText(ann.content || "", {
                x: ann.x,
                y: pageHeight - ann.y,
                size: ann.font_size || 16,
                font,
                color,
              })
              break
            }
            case "checkmark": {
              const chkSize = ann.size || 1.0
              const chkX = ann.x
              const chkY = ann.y
              // Draw the two strokes of the checkmark
              page.drawLine({
                start: { x: chkX - 7 * chkSize, y: pageHeight - chkY },
                end: {
                  x: chkX - 2 * chkSize,
                  y: pageHeight - chkY - 5 * chkSize,
                },
                thickness: 3 * chkSize,
                color,
              })
              page.drawLine({
                start: {
                  x: chkX - 2 * chkSize,
                  y: pageHeight - chkY - 5 * chkSize,
                },
                end: {
                  x: chkX + 7 * chkSize,
                  y: pageHeight - chkY + 5 * chkSize,
                },
                thickness: 3 * chkSize,
                color,
              })
              break
            }
            case "xmark": {
              const xmSize = ann.size || 1.0
              // Draw the two crossing strokes of the X
              page.drawLine({
                start: { x: ann.x - 6 * xmSize, y: pageHeight - ann.y + 6 * xmSize },
                end: { x: ann.x + 6 * xmSize, y: pageHeight - ann.y - 6 * xmSize },
                thickness: 3 * xmSize,
                color,
              })
              page.drawLine({
                start: { x: ann.x - 6 * xmSize, y: pageHeight - ann.y - 6 * xmSize },
                end: { x: ann.x + 6 * xmSize, y: pageHeight - ann.y + 6 * xmSize },
                thickness: 3 * xmSize,
                color,
              })
              break
            }
            case "task": {
              const taskColor = hexToRgb(ann.color || "#FF6B00")
              // Draw rounded rectangle
              page.drawRectangle({
                x: ann.x - 14,
                y: pageHeight - ann.y - 14,
                width: 28,
                height: 28,
                color: taskColor,
                borderColor: rgb(1, 1, 1),
                borderWidth: 2,
              })
              // Draw checkmark
              page.drawLine({
                start: { x: ann.x - 5, y: pageHeight - ann.y - 1 },
                end: { x: ann.x - 1, y: pageHeight - ann.y - 5 },
                thickness: 2.5,
                color: rgb(1, 1, 1),
              })
              page.drawLine({
                start: { x: ann.x - 1, y: pageHeight - ann.y - 5 },
                end: { x: ann.x + 7, y: pageHeight - ann.y + 5 },
                thickness: 2.5,
                color: rgb(1, 1, 1),
              })
              // Draw task name
              if (ann.task_name) {
                page.drawText(ann.task_name, {
                  x: ann.x + 18,
                  y: pageHeight - ann.y + 4,
                  size: 11,
                  font: helveticaBold,
                  color: taskColor,
                })
              }
              // Draw task status below task name
              if (ann.task_status) {
                page.drawText(ann.task_status, {
                  x: ann.x + 18,
                  y: pageHeight - ann.y - 8,
                  size: 9,
                  font: helvetica,
                  color: hexToRgb("#6B7280"),
                })
              }
              break
            }
          }
        })
      })

      const pdfBytes = await pdfDoc.save()
      const blob = new Blob([pdfBytes], { type: "application/pdf" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `annotated_${this.getFilename()}`
      link.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error("PDF export error:", err)
    } finally {
      this.showLoading(false)
    }
  }

  // ──────────────────────────────────────────────────
  // Utilities
  // ──────────────────────────────────────────────────
  get csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }

  generateId() {
    return "ann_" + Math.random().toString(36).substr(2, 9) + Date.now().toString(36)
  }

  getFilename() {
    const pdfUrl = this.pdfUrlValue
    const fallback = this.fileTypeValue === "image" ? "image.png" : "document.pdf"
    try {
      const urlObj = new URL(pdfUrl, window.location.origin)
      const parts = urlObj.pathname.split("/")
      return parts[parts.length - 1] || fallback
    } catch {
      return fallback
    }
  }

  showLoading(show) {
    if (this.hasLoadingSpinnerTarget) {
      this.loadingSpinnerTarget.classList.toggle("hidden", !show)
    }
  }

  updateSaveStatus(status) {
    if (!this.hasSaveStatusTarget) return

    const el = this.saveStatusTarget
    switch (status) {
      case "saving":
        el.textContent = this.i18nSavingValue
        el.className = "text-xs text-yellow-600"
        break
      case "saved":
        el.textContent = this.i18nSavedValue
        el.className = "text-xs text-green-600"
        break
      case "error":
        el.textContent = this.i18nErrorValue
        el.className = "text-xs text-red-600"
        break
    }
  }
}
